import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { useAuth } from '../context/AuthContext';
import { taskAPI, timerAPI, workspaceAPI, dailyStandupAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { TaskDetailFullModal } from '../components/TaskDetailFullModal';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    CONST_WORKFLOW_CONFIG,
} from '../lib/utils';
import { 
    Select, 
    SelectContent, 
    SelectItem, 
    SelectTrigger, 
    SelectValue 
} from '../components/ui/select';
import { 
    cn, 
    formatDate, 
    priorityConfig, 
    isOverdue,
    formatDuration,
    getInitials
} from '../lib/utils';
import {
    CheckCircle2,
    Clock,
    Calendar,
    Play,
    Square,
    AlertTriangle,
    ListTodo,
    Loader2,
    ArrowUpDown,
} from 'lucide-react';
import { toast } from 'sonner';

import TaskListItem from '@/components/TaskListItem';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { useTimerContext } from '@/context/TimerContext';

// Default statuses for task detail modal
const DEFAULT_STATUSES = [
    { name: 'To Do', color: '#6B7280' },
    { name: 'In Progress', color: '#3B82F6' },
    { name: 'Review', color: '#6366F1' },
    { name: 'Done', color: '#10B981' }
];

export default function MyTasksPage() {
    const containerRef = useRef(null);
    const sentinelRef = useRef(null);

    const { user } = useAuth();
    const { 
        currentWorkspace, 
        fetchWorkspaces, 
        selectWorkspace,
        workspaces,
        lists,
        members,
        refreshTasks,
    } = useWorkspace();
    
    const {activeTimer, setActiveTimer} = useTimerContext();
    const [myTasks, setMyTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingTask, setLoadingTask] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [activeTab, setActiveTab] = useState('todo');
    const [standupTasks, setStandupTasks] = useState([]);
    const [standupLoading, setStandupLoading] = useState(false);
    const [sortBy, setSortBy] = useState('priority'); // 'priority', 'due_date', 'created_at'
    const [stats, setStats] = useState({
        total: 0,
        todo: 0,
        in_progress: 0,
        review: 0,
        done: 0
    });

    const [isReset, setIsReset] = useState(true);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);

    // Priority order for sorting
    const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3, none: 4 };

    useEffect(() => {
        initPage();
    }, []);

    useEffect(() => {
        if (!currentWorkspace || !user) return;

        if (isReset) {
            fetchMyTaskStat();   // reset fetch
            fetchMyTasks(true);
            setIsReset(false);
        } else {
            fetchMyTasks(false);  // pagination biasa
        }
    }, [page, currentWorkspace, user, activeTab]);

    // efek workspace change
    useEffect(() => {
        if (currentWorkspace && user) {
            setLoading(true);
            setHasMore(true);
            setIsReset(true);
            setPage(1); // trigger efek [page]
        }
    }, [currentWorkspace, user]);

    // fetch standup tasks — reusable function
    const fetchStandupTasks = useCallback(async () => {
        if (!currentWorkspace?.id || !user?.id) return;
        setStandupLoading(true);
        try {
            // Daily task tetap tampil walau standup nonaktif — endpoint standup balas 403,
            // jadi lewati panggilannya dan cukup ambil DAILY_TASK saja.
            const [summaryRes, tasksRes] = await Promise.all([
                currentWorkspace.daily_standup_enabled
                    ? dailyStandupAPI.getMySummary(currentWorkspace.id)
                    : Promise.resolve(null),
                taskAPI.getByWorkspace(currentWorkspace.id, { assignee_id: user.id, exclude_done: true }),
            ]);
            const planned = summaryRes?.data?.planned_today || [];
            const plannedIds = new Set(planned.map(t => t.id));
            const dailyTasks = (tasksRes.data || []).filter(
                t => t.category === 'DAILY_TASK' && !plannedIds.has(t.id)
            );
            setStandupTasks([...planned, ...dailyTasks]);
        } catch (_) {
            setStandupTasks([]);
        } finally {
            setStandupLoading(false);
        }
    }, [currentWorkspace?.id, currentWorkspace?.daily_standup_enabled, user?.id]);

    // efek standup tab — refresh saat tab aktif
    useEffect(() => {
        if (activeTab !== 'standup') return;
        fetchStandupTasks();
    }, [activeTab, fetchStandupTasks]);




    // Check for active timer
    // useEffect(() => {
    //     checkActiveTimer();
    // }, []);

    const initPage = async () => {
        let ws = currentWorkspace;
        if (!ws) {
            const wsList = await fetchWorkspaces();
            if (wsList.length > 0) {
                await selectWorkspace(wsList[0]);
                ws = wsList[0];
            }
        }
        // Cek apakah user sudah punya rencana hari ini (termasuk daily tasks)
        if (ws?.id && user?.id) {
            try {
                const [summaryRes, tasksRes] = await Promise.all([
                    ws.daily_standup_enabled
                        ? dailyStandupAPI.getMySummary(ws.id)
                        : Promise.resolve(null),
                    taskAPI.getByWorkspace(ws.id, { assignee_id: user.id, exclude_done: true }),
                ]);
                const planned = summaryRes?.data?.planned_today || [];
                const plannedIds = new Set(planned.map(t => t.id));
                const dailyTasks = (tasksRes.data || []).filter(
                    t => t.category === 'DAILY_TASK' && !plannedIds.has(t.id)
                );
                const combined = [...planned, ...dailyTasks];
                if (combined.length > 0) {
                    setStandupTasks(combined);
                    setActiveTab('standup');
                }
            } catch (_) {}
        }
        setLoading(false);
    };

    const fetchMyTaskStat = async () => {
        try {
            const response = await workspaceAPI.getMyTaskStats(currentWorkspace?.id);
            setStats(response.data);
        } catch (error) {
            console.error('Failed to fetch my tasks:', error);
        }
    };

    const fetchMyTasks = async (changedTab = true) => {
        if (activeTab === 'standup') return;
        if (loadingTask || !currentWorkspace) return;
        if(changedTab) setLoadingTask(true);
        const status = getStatusGroupByActiveTab();
        try {
            // Fetch ALL tasks assigned to user (all statuses)
            const response = await taskAPI.getByWorkspace(currentWorkspace?.id, {
                assignee_id: user.id,
                group: status,
                page: page,
                size: pageSize
            });
            
            const newTasks = response.data;
            // Append data baru
            if(changedTab) {
                setMyTasks(newTasks);
                setIsReset(false);
            }else{
                setMyTasks(prev => [...prev, ...newTasks]);
            }

            // Jika jumlah data < pageSize, berarti sudah tidak ada lagi
            if (newTasks.length < pageSize) {
                setHasMore(false);
            }

        } catch (error) {
            console.error('Failed to fetch my tasks:', error);
        } finally {
            setLoadingTask(false);
            setLoading(false);
        }
    };

    const handleTabChange = (val) => {
        setActiveTab(val);
        setHasMore(true);
        setIsReset(true);
        setPage(1); // trigger efek [page]
    };





    

    const loadMore = useCallback(() => {
        console.log("Load more triggered!");
        setPage(prev => prev + 1);
    }, []);

    // Hook infinite scroll untuk Radix viewport
    useInfiniteScroll(containerRef, sentinelRef, loadMore, hasMore, loadingTask);




    const handleTaskClick = (task) => {
        setSelectedTask(task);
        setIsModalOpen(true);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setSelectedTask(null);
        if (activeTab === 'standup') {
            fetchStandupTasks();
        } else {
            fetchMyTasks();
        }
    };

    const handleStartTimer = async (e, task) => {
        e.stopPropagation();
        try {
            const response = await timerAPI.start(task.id);
            setActiveTimer(response.data);
            toast.success(`Timer started for "${task.title}"`);
            fetchMyTasks();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to start timer');
        }
    };

    const handleStopTimer = async (e, task) => {
        e.stopPropagation();
        try {
            await timerAPI.stop();
            setActiveTimer(null);
            toast.success('Timer stopped');
            fetchMyTasks();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to stop timer');
        }
    };


    const handleQuickStatusChange = async (taskId) => {
        try {
            await taskAPI.quickChangeStatus(taskId);
            if (activeTab === 'standup') {
                fetchStandupTasks();
            } else {
                fetchMyTasks();
            }
            toast.success('Status updated');
        } catch (error) {
            toast.error('Failed to update status');
        }
    };


    const getStatusGroupByActiveTab = () => {
        switch (activeTab) {
            case 'todo':
                return CONST_WORKFLOW_CONFIG.notstarted.name;
            case 'in-progress':
                return CONST_WORKFLOW_CONFIG.active.name;
            case 'review':
                return CONST_WORKFLOW_CONFIG.review.name;
            case 'overdue':
                return 'overdue';
            case 'completed':
                return CONST_WORKFLOW_CONFIG.done.name;
            default: // 'all'
                return null; // Show all statuses
        }
    }

    // Sort tasks - DAILY_TASK first, then Priority order: urgent > high > medium > low > none
    const sortedTasks = [...myTasks].sort((a, b) => {
        // Daily tasks always on top
        const aDaily = a.category === 'DAILY_TASK' ? 0 : 1;
        const bDaily = b.category === 'DAILY_TASK' ? 0 : 1;
        if (aDaily !== bDaily) return aDaily - bDaily;

        switch (sortBy) {
            case 'priority':
                const priorityA = priorityOrder[a.priority?.toLowerCase()] ?? 4;
                const priorityB = priorityOrder[b.priority?.toLowerCase()] ?? 4;
                return priorityA - priorityB;
            case 'due_date':
                if (!a.due_date) return 1;
                if (!b.due_date) return -1;
                return new Date(a.due_date) - new Date(b.due_date);
            case 'created_at':
                return new Date(b.created_at) - new Date(a.created_at);
            default:
                const defPriorityA = priorityOrder[a.priority?.toLowerCase()] ?? 4;
                const defPriorityB = priorityOrder[b.priority?.toLowerCase()] ?? 4;
                return defPriorityA - defPriorityB;
        }
    });

    if (loading) {
        return (
            <Layout title="My Task">
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
                </div>
            </Layout>
        );
    }

    if (!currentWorkspace) {
        return (
            <Layout title="My Task">
                <div className="empty-state">
                    <p className="text-brand-text-muted">Please select a workspace first</p>
                </div>
            </Layout>
        );
    }

    return (
        <Layout title="My Task" subtitle={`Tasks assigned to ${user?.name}`}>
            <div className="space-y-6" data-testid="my-tasks-page">
                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card className="border-brand-border shadow-card">
                        <CardContent className="p-5">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-brand-text-secondary">Total Task</p>
                                    <p className="text-2xl font-bold text-brand-text-primary">{stats?.total_tasks}</p>
                                </div>
                                <ListTodo className="w-8 h-8 text-brand-primary opacity-50" strokeWidth={1.5} />
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-brand-border shadow-card">
                        <CardContent className="p-5">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-brand-text-secondary">In Progress</p>
                                    <p className="text-2xl font-bold text-brand-info">{stats?.active}</p>
                                </div>
                                <Clock className="w-8 h-8 text-brand-info opacity-50" strokeWidth={1.5} />
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-brand-border shadow-card">
                        <CardContent className="p-5">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-brand-text-secondary">Overdue</p>
                                    <p className="text-2xl font-bold text-brand-error">{stats?.overdue_tasks}</p>
                                </div>
                                <AlertTriangle className="w-8 h-8 text-brand-error opacity-50" strokeWidth={1.5} />
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-brand-border shadow-card">
                        <CardContent className="p-5">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-brand-text-secondary">Completed</p>
                                    <p className="text-2xl font-bold text-brand-success">{stats?.done}</p>
                                </div>
                                <CheckCircle2 className="w-8 h-8 text-brand-success opacity-50" strokeWidth={1.5} />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Tasks Tabs */}
                <Tabs value={activeTab} onValueChange={handleTabChange}>
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <TabsList className="bg-brand-accent-soft/50">
                            <TabsTrigger value="standup">
                                Rencana Hari Ini ({standupTasks.length})
                            </TabsTrigger>
                            <TabsTrigger value="todo">To Do ({stats?.not_started})</TabsTrigger>
                            <TabsTrigger value="in-progress">In Progress ({stats?.active})</TabsTrigger>
                            <TabsTrigger value="review">Review ({stats?.review})</TabsTrigger>
                            <TabsTrigger value="overdue" className="text-brand-error">
                                Overdue ({stats?.overdue_tasks})
                            </TabsTrigger>
                            <TabsTrigger value="completed">Done ({stats?.done})</TabsTrigger>
                            <TabsTrigger value="all">All ({stats?.total_tasks})</TabsTrigger>
                        </TabsList>

                        {/* Sort Dropdown */}
                        <div className="flex items-center gap-2">
                            <ArrowUpDown className="w-4 h-4 text-brand-text-muted" />
                            <Select value={sortBy} onValueChange={setSortBy}>
                                <SelectTrigger className="w-40" data-testid="sort-select">
                                    <SelectValue placeholder="Sort by..." />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="priority">Priority</SelectItem>
                                    <SelectItem value="due_date">Due Date</SelectItem>
                                    <SelectItem value="created_at">Newest First</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <TabsContent value="standup" className="mt-4">
                        {standupLoading ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
                            </div>
                        ) : standupTasks.length === 0 ? (
                            <div className="empty-state py-12">
                                <Calendar className="w-12 h-12 text-brand-text-muted mb-4" strokeWidth={1.5} />
                                <p className="text-brand-text-muted">Belum ada rencana task untuk hari ini</p>
                            </div>
                        ) : (
                            <Card className="border-brand-border">
                                <div className="divide-y divide-brand-border overflow-y-auto sticky h-[calc(100vh-300px)]">
                                    {standupTasks.map(task => (
                                        <TaskListItem
                                            key={task.id}
                                            task={task}
                                            onClick={() => handleTaskClick(task)}
                                            onStatusChange={() => handleQuickStatusChange(task.id)}
                                            onStartTimer={handleStartTimer}
                                            onStopTimer={handleStopTimer}
                                            isTimerActive={activeTimer?.task_id === task.id}
                                        />
                                    ))}
                                </div>
                            </Card>
                        )}
                    </TabsContent>

                    {activeTab !== 'standup' && (
                    <TabsContent value={activeTab} className="mt-4">
                        {
                        loadingTask ? (
                            <div className="flex items-center justify-center empty-state py-12 w-full h-[calc(100vh-300px)]">
                                <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
                            </div>
                        ): 
                        sortedTasks.length > 0 ? (
                            <Card className="border-brand-border" >
                                <div className="divide-y divide-brand-border overflow-y-auto sticky h-[calc(100vh-300px)]" ref={containerRef} >
                                    {sortedTasks.map((task, index) => (
                                        <>
                                            <TaskListItem
                                            key={task.id}
                                            task={task}
                                            onClick={() => handleTaskClick(task)}
                                            onStatusChange={() => handleQuickStatusChange(task.id)}
                                            onStartTimer={handleStartTimer}
                                            onStopTimer={handleStopTimer}
                                            isTimerActive={activeTimer?.task_id === task.id}
                                            />
                                        </>
                                    ))}
                                    <div ref={sentinelRef} style={{ height: 1, opacity: 0 }} />
                                </div>
                            </Card>
                        ) : (
                            <div className="empty-state py-12">
                                <ListTodo className="w-12 h-12 text-brand-text-muted mb-4" strokeWidth={1.5} />
                                <p className="text-brand-text-muted">No tasks in this category</p>
                            </div>
                        )}
                    </TabsContent>
                    )}
                </Tabs>
            </div>

            {/* Task Detail Full Modal */}
            {selectedTask && isModalOpen && (
                <TaskDetailFullModal
                    isOpen={isModalOpen}
                    onClose={handleModalClose}
                    task={selectedTask}
                    listId={selectedTask?.list_id}
                    statuses={lists.find(l => l.id === selectedTask?.list_id)?.statuses || DEFAULT_STATUSES}
                    members={members}
                    onTaskUpdate={() => {
                        if (activeTab === 'standup') {
                            fetchStandupTasks();
                        } else {
                            refreshTasks();
                        }
                        // Refresh active timer
                        timerAPI.getActive().then(res => {
                            setActiveTimer(res.data || null);
                        }).catch(() => setActiveTimer(null));
                    }}
                />
            )}
        </Layout>
    );
}
